import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Generic action dialog used by every dashboard CTA.
 * fields: [{name,label,type,required,options,placeholder,defaultValue}]
 * onSubmit receives the form values; returns the created object (toasted).
 */
export function ActionDialog({ open, onOpenChange, title, description, fields, submitLabel = "Create", endpoint, method = "POST", onSuccess, testidPrefix = "act" }) {
  const initial = Object.fromEntries(fields.map(f => [f.name, f.defaultValue ?? (f.type === "number" ? 0 : "")]));
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => { if (open) setForm(initial); }, [open]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => {
          const f = fields.find(x => x.name === k);
          if (f?.type === "number") return [k, Number(v) || 0];
          if (f?.type === "csv") return [k, String(v).split(",").map(s => s.trim()).filter(Boolean)];
          return [k, v];
        })
      );
      const { data } = await api.request({ url: endpoint, method, data: payload });
      toast.success(`${title} saved`);
      onSuccess?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          {fields.map((f) => (
            <div key={f.name}>
              <Label className="text-xs">{f.label}{f.required && <span className="text-rose-500"> *</span>}</Label>
              {f.type === "select" ? (
                <Select value={form[f.name]} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                  <SelectTrigger className="mt-1" data-testid={`${testidPrefix}-${f.name}`}><SelectValue placeholder={f.placeholder || "Select…"} /></SelectTrigger>
                  <SelectContent>{(f.options || []).map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              ) : f.type === "textarea" ? (
                <Textarea required={f.required} rows={f.rows || 5} value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} placeholder={f.placeholder} data-testid={`${testidPrefix}-${f.name}`} className="mt-1" />
              ) : (
                <Input required={f.required} type={f.type === "number" ? "number" : "text"} value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} placeholder={f.placeholder} data-testid={`${testidPrefix}-${f.name}`} className="mt-1" min={f.min} max={f.max} maxLength={f.maxLength} />
              )}
              {f.help && <p className="mt-1 text-[11px] text-slate-500">{f.help}</p>}
            </div>
          ))}
          <DialogFooter className="pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid={`${testidPrefix}-submit`}>
              {busy ? <><Loader2 className="size-4 animate-spin" /> Saving…</> : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Simple read-only modal for displaying transcripts / details */
export function InfoDialog({ open, onOpenChange, title, description, children, testid = "info-dialog" }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid={testid}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="mt-2">{children}</div>
      </DialogContent>
    </Dialog>
  );
}
